import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessageSquarePlus, Plus, RefreshCw } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useCommunications, useCommunicationsCount } from '@/hooks/useCommunications';
import { CommunicationCard } from '@/components/communications/CommunicationCard';
import { useCustomerIdsByPhone } from '@/hooks/useCustomers';
import { LogsTable } from '@/components/communications/LogsTable';
import { CommunicationFilters } from '@/components/communications/CommunicationFilters';
import { LogCommunicationDialog } from '@/components/communications/LogCommunicationDialog';
import { NewMessageDialog } from '@/components/communications/NewMessageDialog';
import { OmniChannelTab } from '@/components/communications/OmniChannelTab';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { CommunicationFilters as FilterType } from '@/types/communications';
import { DEFAULT_FILTERS } from '@/types/communications';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

const pillTabClass =
  'rounded-full px-4 py-1.5 text-xs font-semibold data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=active]:shadow-none';

export default function Communications() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial tab from URL or default to omni-channel
  const initialTab = searchParams.get('tab') || 'omni-channel';
  const [activeTab, setActiveTab] = useState(initialTab);

  const [filters, setFilters] = useState<FilterType>(DEFAULT_FILTERS);
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [showNewMessageDialog, setShowNewMessageDialog] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 50;

  // Sync URL with tab changes, reset pagination, and force refetch
  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
    setOffset(0); // Reset pagination when switching tabs
    // Force refetch when switching to logs or timeline tab
    if (value === 'logs' || value === 'timeline') {
      queryClient.invalidateQueries({ queryKey: ['communications'] });
      queryClient.invalidateQueries({ queryKey: ['communications-count'] });
    }
  }, [setSearchParams, queryClient]);

  // Debounce search filter
  const debouncedFilters = {
    ...filters,
    search: useDebouncedValue(filters.search, 300),
  };

  const { data, isLoading, isFetching, isError, error } = useCommunications({
    filters: debouncedFilters,
    limit,
    offset,
    enabled: activeTab === 'timeline' || activeTab === 'logs',
  });

  const { data: totalCount } = useCommunicationsCount(debouncedFilters);

  // One lookup for the whole page: links the caller name through to the
  // customer card even when the log row has no customer_id of its own.
  const resolveCustomerId = useCustomerIdsByPhone(data?.communications);

  // Auto-recover: reset offset if we're on an empty page but total count > 0
  useEffect(() => {
    if (totalCount && totalCount > 0 && data?.communications.length === 0 && offset > 0) {
      setOffset(0);
    }
  }, [totalCount, data?.communications.length, offset]);

  const handleFiltersChange = useCallback((newFilters: FilterType) => {
    setFilters(newFilters);
    setOffset(0); // Reset pagination when filters change
  }, []);

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['communications'] });
    queryClient.invalidateQueries({ queryKey: ['communications-count'] });
    queryClient.invalidateQueries({ queryKey: ['omni-channel-threads'] });
  }, [queryClient]);

  const handleLoadMore = useCallback(() => {
    if (data?.hasMore) {
      setOffset((prev) => prev + limit);
    }
  }, [data?.hasMore, limit]);

  return (
    <Layout>
      <div className="container mx-auto p-6 max-w-6xl">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold tracking-[-0.02em]">Communications</h1>
              <p className="mt-1 text-[13px] text-muted-foreground">
                {activeTab === 'omni-channel'
                  ? 'Unified messaging inbox - SMS and email conversations'
                  : activeTab === 'timeline'
                    ? 'View and manage all customer interactions'
                    : 'Read-only archive of all communication logs'
                }
              </p>
            </div>
            <div className="flex items-center gap-2">
              {activeTab === 'omni-channel' && (
                <Button onClick={() => setShowNewMessageDialog(true)}>
                  <MessageSquarePlus className="h-4 w-4 mr-1" />
                  New message
                </Button>
              )}
              {(activeTab === 'timeline' || activeTab === 'logs') && (
                <>
                  <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isFetching}>
                    <RefreshCw className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  {activeTab === 'timeline' && (
                    <Button onClick={() => setShowLogDialog(true)}>
                      <Plus className="h-4 w-4 mr-1" />
                      Log Communication
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Tabs — pill segmented control */}
          <TabsList className="mb-6 h-auto gap-1 rounded-full border border-border bg-card p-1">
            <TabsTrigger value="omni-channel" className={pillTabClass}>
              Omni-Channel
            </TabsTrigger>
            <TabsTrigger value="timeline" className={pillTabClass}>
              Timeline
            </TabsTrigger>
            <TabsTrigger value="logs" className={pillTabClass}>
              Logs
            </TabsTrigger>
          </TabsList>

          {/* Omni-Channel Tab */}
          <TabsContent value="omni-channel" className="mt-0">
            <OmniChannelTab />
          </TabsContent>

          {/* Timeline Tab - Filters only shown here */}
          <TabsContent value="timeline" className="mt-0">
            <div className="mb-6">
              <CommunicationFilters filters={filters} onFiltersChange={handleFiltersChange} />
            </div>
            <div className="space-y-3">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-32 w-full" />
                ))
              ) : isError ? (
                <div className="text-center py-12">
                  <p className="text-lg text-destructive">Failed to load timeline</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    {error instanceof Error ? error.message : 'An unexpected error occurred'}
                  </p>
                  <Button variant="outline" onClick={handleRefresh}>
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Retry
                  </Button>
                </div>
              ) : !data?.communications?.length ? (
                <div className="text-center py-12 text-muted-foreground">
                  <p className="text-lg">No communications found</p>
                  <p className="text-sm">Try adjusting your filters or date range</p>
                </div>
              ) : (
                <>
                  {data.communications.map((communication) => (
                    <CommunicationCard
                      key={`${communication.source}-${communication.id}`}
                      communication={communication}
                      customerId={resolveCustomerId(communication)}
                    />
                  ))}

                  {/* Load More / Pagination */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Showing {data.communications.length} of {totalCount?.toLocaleString() || '...'} communications
                    </p>
                    {data.hasMore && (
                      <Button variant="outline" onClick={handleLoadMore} disabled={isFetching}>
                        {isFetching ? 'Loading...' : 'Load More'}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs" className="mt-0">
            <div className="mb-6">
              <CommunicationFilters filters={filters} onFiltersChange={handleFiltersChange} />
            </div>
            {isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 10 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : isError ? (
              <div className="text-center py-12">
                <p className="text-lg text-destructive">Failed to load logs</p>
                <p className="text-sm text-muted-foreground mb-4">
                  {error instanceof Error ? error.message : 'An unexpected error occurred'}
                </p>
                <Button variant="outline" onClick={handleRefresh}>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Retry
                </Button>
              </div>
            ) : data?.communications.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <p className="text-lg">No logs found</p>
                <p className="text-sm">Try adjusting your filters or date range</p>
              </div>
            ) : (
              <>
                <LogsTable communications={data?.communications || []} />

                {/* Load More / Pagination */}
                <div className="flex items-center justify-between pt-4 mt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Showing {data?.communications.length || 0} of {totalCount?.toLocaleString() || '...'} logs
                  </p>
                  {data?.hasMore && (
                    <Button variant="outline" onClick={handleLoadMore} disabled={isFetching}>
                      {isFetching ? 'Loading...' : 'Load More'}
                    </Button>
                  )}
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* Log Communication Dialog */}
        <LogCommunicationDialog
          open={showLogDialog}
          onOpenChange={setShowLogDialog}
        />

        {/* New Message Dialog */}
        <NewMessageDialog
          open={showNewMessageDialog}
          onOpenChange={setShowNewMessageDialog}
        />

      </div>
    </Layout>
  );
}
